import React, { useEffect, useState, useMemo } from 'react';
import { View, Text, FlatList, RefreshControl, StyleSheet } from 'react-native';
import { showPhotoPickerActionSheet } from '@/utils/common/alert';
import { useNavigation } from '@react-navigation/native';
import { useResidentConversation, useResidentMessaging } from '@/hooks/residents/useMessages';
import { useMarkConversationAsReadMutation } from '@/store/api/admin/messagesApi';
import type { IChatWindowProps, IMessage } from '@/types/admin/messages';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ImageViewerModal } from '@/components/common/ImageViewerModal';
import { MessageBubbleMobile } from './MessageBubbleMobile';
import { CHAT_MOBILE_COLORS } from '@/constants/residents/mobile/chatColors';
import { useAppSelector } from '@/store';
import { formatFullHeaderDate } from '@/utils/common/formatting';
import MessageInputBar from './MessageInputBar';
import AttachmentPreview from './AttachmentPreview';
import { useChatAttachments } from '@/hooks/residents/useChatAttachments';
import { buildMessageFormData } from '@/services/residents/messages/formData';
import { RESIDENTS_MOBILE_MESSAGE_MAX_LENGTH } from '@/constants/residents/mobile/message';
import { groupMessages, type MessageGroup } from '@/utils/residents/messages/groupMessages';
import { useAutoScrollToBottom } from '@/hooks/residents/useAutoScrollToBottom';
import AttachmentMessageMobile from './AttachmentMessageMobile';
import { useToast } from '@/contexts/admin/ToastContext';

export const ResidentChatWindowMobile: React.FC<IChatWindowProps> = ({ conversationId }) => {
  const { conversation, isLoading, error, refetch } = useResidentConversation(conversationId);
  const { sendMessage, isLoading: isSending } = useResidentMessaging() as any;
  const [markAsRead] = useMarkConversationAsReadMutation();
  const authUserId = useAppSelector((s) => s.auth.user?.id);
  const navigation = useNavigation();
  const { showError } = useToast();

  const [messageText, setMessageText] = useState('');
  const { attachments, addFromCamera, addFromLibrary, removeAttachment, resetAttachments, handleImageResponse } = useChatAttachments();
  const [refreshing, setRefreshing] = useState(false);
  const { listRef: flatListRef, isNearBottomRef, scrollToBottom: forceScrollToBottom, handleScroll } = useAutoScrollToBottom<FlatList>();

  // Image viewer modal state
  const [viewerVisible, setViewerVisible] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [viewerImages, setViewerImages] = useState<string[]>([]);
  // Removed manual header offset; KeyboardSafeView handles header height internally
  
  // Track if conversation has been marked as read to avoid duplicate calls
  const hasMarkedAsReadRef = React.useRef<number | null>(null);

  // Mark conversation as read when user has viewed messages
  // Deferred to ensure user actually sees the content, not just on load
  useEffect(() => {
    if (!conversationId || !conversation) return;
    
    // Reset tracking when conversation changes
    if (hasMarkedAsReadRef.current !== conversationId) {
      hasMarkedAsReadRef.current = null;
    }
    
    // Already marked this conversation as read
    if (hasMarkedAsReadRef.current === conversationId) return;
    
    // Mark as read after a short delay to ensure content is visible
    // This prevents marking as read before the user can actually see messages
    const timeoutId = setTimeout(() => {
      if (hasMarkedAsReadRef.current !== conversationId) {
        hasMarkedAsReadRef.current = conversationId;
        markAsRead(conversationId).catch(() => {
          // Silently fail - not critical, reset so we can retry
          hasMarkedAsReadRef.current = null;
        });
      }
    }, 2000);
    
    return () => clearTimeout(timeoutId);
  }, [conversationId, conversation, markAsRead]);

  const messageGroups: MessageGroup<IMessage>[] = useMemo(() =>
    groupMessages<IMessage>(conversation?.messages as IMessage[], {
      getHeaderDate: (message) => formatFullHeaderDate(message.createdAt),
      sortDaysBy: 'firstMessageCreatedAt',
    }),
  [conversation?.messages]);

  // forceScrollToBottom provided by useAutoScrollToBottom

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (messageGroups.length > 0) {
      timeoutId = setTimeout(() => forceScrollToBottom(true), 150);
    }
    return () => { if (timeoutId) clearTimeout(timeoutId); };
  }, [messageGroups, forceScrollToBottom]);

  // keyboard-driven scroll handled via useAutoScrollToBottom

  const handleSendMessage = async () => {
    if (isSending || (!messageText.trim() && attachments.length === 0)) return;
    const formData = buildMessageFormData({ content: messageText.trim(), isAdmin: false, files: attachments as any });
    try {
      await sendMessage({ conversationId, body: formData }).unwrap();
      setMessageText('');
      resetAttachments();
      // Immediately refresh conversation after send to mirror iOS behavior
      try { await refetch(); } catch {}
      setTimeout(() => forceScrollToBottom(false), 300);
    } catch (e) {
      console.error('Failed to send message:', e);
      const msg = (e as any)?.data?.message || (e as any)?.error || 'Failed to send message. Please try again.';
      showError(msg);
    }
  };

  const renderAttachmentMessage = (message: IMessage) => (
    <AttachmentMessageMobile
      key={`files-${message.id}`}
      message={message}
      authUserId={authUserId}
      onOpenImageViewer={(urls, startIndex) => { setViewerImages(urls); setViewerIndex(startIndex); setViewerVisible(true); }}
      onOpenDocument={(url, title) => (navigation as any).navigate('WebView', { url, title })}
    />
  );

  const renderMessageGroup = ({ item }: { item: MessageGroup<IMessage> }) => (
    <View style={styles.messageGroup}>
      <View style={styles.dateHeader}><Text style={styles.dateText}>{item.date}</Text></View>
      {item.messages.map((message) => (
        <React.Fragment key={message.id}>
          {!!message.content && (
            <MessageBubbleMobile message={message} authUserId={authUserId} />
          )}
          {!!message.files?.length && renderAttachmentMessage(message)}
        </React.Fragment>
      ))}
    </View>
  );

  const renderMessageInput = () => (
    <>
      {attachments.length > 0 && (
        <AttachmentPreview attachments={attachments as any} onRemove={removeAttachment} />
      )}
      <MessageInputBar
        messageText={messageText}
        setMessageText={setMessageText}
        onSend={handleSendMessage}
        disabled={isSending || (!messageText.trim() && attachments.length === 0)}
        isSending={isSending}
        maxLength={RESIDENTS_MOBILE_MESSAGE_MAX_LENGTH}
        onPressPaperclip={() => {
          showPhotoPickerActionSheet(addFromLibrary, addFromCamera);
        }}
        onFocusScrollToBottom={() => setTimeout(() => forceScrollToBottom(false), 200)}
      />
    </>
  );

  if (isLoading) {
    return <LoadingSpinner message="Loading conversation..." size="large" showMessage={true} />;
  }
  if (error) {
    return (<View style={styles.errorContainer}><Text>Failed to load conversation</Text></View>);
  }
  if (!conversation) {
    return (
      <View style={styles.emptyContainer}>
        <Text style={styles.emptyText}>Select a conversation to start messaging</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.messagesContainer}>
        <FlatList
          ref={flatListRef}
          data={messageGroups}
          renderItem={renderMessageGroup}
          keyExtractor={(item) => `${item.date}:${item.messages.length}`}
          style={styles.messagesList}
          contentContainerStyle={styles.messagesContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="none"
          scrollEventThrottle={16}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={async () => { setRefreshing(true); try { await refetch(); } finally { setRefreshing(false); } }} tintColor={CHAT_MOBILE_COLORS.textPrimary} />}
          onScroll={(e) => handleScroll(e.nativeEvent.contentOffset.y, e.nativeEvent.layoutMeasurement.height, e.nativeEvent.contentSize.height, 24)}
          onContentSizeChange={() => isNearBottomRef.current && forceScrollToBottom(true)}
          onLayout={() => setTimeout(() => { if (isNearBottomRef.current) forceScrollToBottom(true); }, 100)}
        />
      </View>
      {renderMessageInput()}
      
      {/* Image Viewer Modal */}
      <ImageViewerModal
        visible={viewerVisible}
        images={viewerImages}
        initialIndex={viewerIndex}
        onClose={() => setViewerVisible(false)}
      />
    </View>
  );
};

export default ResidentChatWindowMobile;


const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: CHAT_MOBILE_COLORS.cardBackground },
  messagesContainer: { flex: 1 },
  messagesList: { flex: 1 },
  messagesContent: { paddingVertical: 8, paddingHorizontal: 24 },
  dateHeader: { alignItems: 'center', paddingVertical: 20 },
  dateText: { fontSize: 12, fontWeight: '300', color: CHAT_MOBILE_COLORS.textPlaceholder, lineHeight: 18 },
  messageGroup: { marginBottom: 16 },
  errorContainer: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: CHAT_MOBILE_COLORS.cardBackground },
  emptyContainer: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24, backgroundColor: CHAT_MOBILE_COLORS.cardBackground },
  emptyText: { marginTop: 8, color: CHAT_MOBILE_COLORS.textMuted },
});